/**
 * Servicio para Meta Pixel y Conversions API
 * Trackea usuarios que vienen desde anuncios de Instagram/Meta
 */
class MetaPixelService {
    constructor() {
        this.pixelId = null; // Se configurará dinámicamente
        // ⚠️ NUNCA poner access tokens en el frontend - se manejan en el backend
        this.initialized = false;
        this.userTrackingData = {};
        this.sessionId = this.generateSessionId();

        // Capturar parámetros UTM y de campaña al cargar
        this.captureUtmParameters();
        this.captureFacebookParameters();

        // Intentar reenviar eventos fallidos cuando se inicializa
        setTimeout(() => {
            this.retryFailedEvents();
        }, 5000); // Esperar 5 segundos después de inicializar
    }

    /**
     * Generar ID de sesión único
     */
    generateSessionId() {
        return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    }

    /**
     * Capturar parámetros UTM de la URL
     */
    captureUtmParameters() {
        const urlParams = new URLSearchParams(window.location.search);

        this.userTrackingData.campaign = {
            utm_source: urlParams.get('utm_source'),
            utm_medium: urlParams.get('utm_medium'),
            utm_campaign: urlParams.get('utm_campaign'),
            utm_content: urlParams.get('utm_content'),
            utm_term: urlParams.get('utm_term'),
            gclid: urlParams.get('gclid'),
            fbclid: urlParams.get('fbclid')
        };

        // Guardar en localStorage para persistencia
        localStorage.setItem('transferIsOk_campaign_data', JSON.stringify(this.userTrackingData.campaign));
    }

    /**
     * Capturar parámetros específicos de Facebook
     */
    captureFacebookParameters() {
        // Obtener fbp (Facebook Pixel cookie)
        const fbpMatch = document.cookie.match(/(?:^|;)\s*_fbp=([^;]*)/);
        this.userTrackingData.fbp = fbpMatch ? fbpMatch[1] : null;

        // Obtener fbc (Facebook Click ID)
        const urlParams = new URLSearchParams(window.location.search);
        const fbclid = urlParams.get('fbclid');
        if (fbclid) {
            this.userTrackingData.fbc = `fb.1.${Date.now()}.${fbclid}`;
        }

        // Datos adicionales del cliente
        this.userTrackingData.client_ip_address = null; // Se captura en el servidor
        this.userTrackingData.client_user_agent = navigator.userAgent;
        this.userTrackingData.browser_name = this.getBrowserName();
        this.userTrackingData.referrer = document.referrer;
        this.userTrackingData.landing_page = window.location.href;
    }

    /**
     * Obtener nombre del navegador
     */
    getBrowserName() {
        const userAgent = navigator.userAgent;
        if (userAgent.indexOf('Chrome') > -1) return 'Chrome';
        if (userAgent.indexOf('Firefox') > -1) return 'Firefox';
        if (userAgent.indexOf('Safari') > -1) return 'Safari';
        if (userAgent.indexOf('Edge') > -1) return 'Edge';
        return 'Unknown';
    }

    /**
     * Inicializar Meta Pixel
     */
    initializePixel(pixelId) {
        if (this.initialized) return;

        this.pixelId = pixelId;

        // Cargar Meta Pixel
        !function (f, b, e, v, n, t, s) {
            if (f.fbq) return; n = f.fbq = function () {
                n.callMethod ?
                    n.callMethod.apply(n, arguments) : n.queue.push(arguments)
            };
            if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = '2.0';
            n.queue = []; t = b.createElement(e); t.async = !0;
            t.src = v; s = b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t, s)
        }(window, document, 'script',
            'https://connect.facebook.net/en_US/fbevents.js');

        // Sin esto el píxel mira solo los clicks y los formularios de la página, y sigue
        // cargado si en la misma pestaña se pasa del registro al home. Tiene que ir antes
        // del init. Solo se manda lo que se pide con fbq('track').
        window.fbq('set', 'autoConfig', false, pixelId);

        // Inicializar con el Pixel ID y configuración de dominio
        window.fbq('init', pixelId, {
            // Configuración para dominio verificado
            external_id: this.sessionId,
            // Mejorar la calidad de los datos
            em: 'disabled', // Deshabilitar hash automático de email
            ph: 'disabled', // Deshabilitar hash automático de teléfono
            fn: 'disabled', // Deshabilitar hash automático de nombre
            ln: 'disabled'  // Deshabilitar hash automático de apellido
        });

        // Configurar dominio verificado
        window.fbq('set', 'agent', 'transferisok', pixelId);

        // Enviar PageView automáticamente
        window.fbq('track', 'PageView');

        this.initialized = true;
        console.log('Meta Pixel inicializado:', pixelId);
    }

    /**
     * Trackear evento de visualización de contenido
     */
    trackViewContent(contentData = {}) {
        console.log('Tracking ViewContent:', contentData);

        // Evento para Meta Pixel
        if (window.fbq) {
            window.fbq('track', 'ViewContent', {
                content_type: contentData.content_type || 'registration_form',
                content_name: contentData.content_name || 'Transfer Is OK - Registro',
                content_category: contentData.content_category || 'Registration',
                value: contentData.value || 0,
                currency: 'ARS'
            });
        }

        // Enviar también a Conversions API
        this.sendConversionEvent('ViewContent', contentData);
    }

    /**
     * Trackear inicio de checkout/registro
     */
    trackInitiateCheckout(checkoutData = {}) {
        console.log('Tracking InitiateCheckout:', checkoutData);

        if (window.fbq) {
            window.fbq('track', 'InitiateCheckout', {
                content_type: checkoutData.content_type || 'registration_step',
                content_name: checkoutData.content_name || 'Transfer Is OK - Inicio Registro',
                value: checkoutData.value || 0,
                currency: 'ARS'
            });
        }

        this.sendConversionEvent('InitiateCheckout', checkoutData);
    }

    /**
     * Trackear Lead (primer paso del registro)
     */
    trackLead(leadData = {}) {
        console.log('Tracking Lead');

        if (window.fbq) {
            window.fbq('track', 'Lead', {
                content_name: leadData.content_name || 'Transfer Is OK - Datos Básicos',
                content_category: leadData.content_category || 'Registration Step 1',
                value: leadData.value || 0,
                currency: 'ARS'
            });
        }

        this.sendConversionEvent('Lead', leadData);
    }

    /**
     * Trackear registro completo
     */
    trackCompleteRegistration(registrationData = {}) {
        console.log('Tracking CompleteRegistration:', registrationData);

        if (window.fbq) {
            window.fbq('track', 'CompleteRegistration', {
                content_name: registrationData.content_name || 'Transfer Is OK - Registro Completo',
                content_category: registrationData.content_category || 'Registration Complete',
                value: registrationData.value || 4000, // Valor estimado del registro completo
                currency: 'ARS'
            });
        }

        this.sendConversionEvent('CompleteRegistration', registrationData);
    }

    /**
     * Trackear evento personalizado
     */
    trackCustomEvent(eventName, eventData = {}) {
        console.log('Tracking Custom Event:', eventName, eventData);

        if (window.fbq) {
            window.fbq('trackCustom', eventName, eventData);
        }

        this.sendConversionEvent(eventName, eventData);
    }

    /**
     * Enviar evento a Meta Conversions API con retry logic
     */
    async sendConversionEvent(eventName, eventData = {}, retryCount = 0) {
        const maxRetries = 3;
        const retryDelay = Math.pow(2, retryCount) * 1000; // Exponential backoff: 1s, 2s, 4s

        try {
            const eventPayload = {
                event_name: eventName,
                event_time: Math.floor(Date.now() / 1000),
                event_source_url: window.location.href,
                action_source: 'website',
                event_id: `${eventName}_${this.sessionId}_${Date.now()}`,
                user_data: this.prepareUserData(),
                custom_data: {
                    // Solo incluir campos estándar que Meta reconoce
                    content_name: eventData.content_name || document.title,
                    content_category: eventData.content_category || 'registration',
                    // Campos adicionales seguros
                    ...(eventData.value && { value: eventData.value }),
                    ...(eventData.currency && { currency: eventData.currency }),
                    ...(eventData.content_type && { content_type: eventData.content_type })
                }
            };

            // Enviar a través de Firebase Functions
            console.log(`🚀 Enviando datos a Meta Conversions API (intento ${retryCount + 1}/${maxRetries + 1}):`, eventPayload);

            // Crear AbortController para timeout
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 segundos timeout

            const response = await fetch('https://us-central1-transferisok.cloudfunctions.net/sendMetaConversion', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(eventPayload),
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const result = await response.json();
            console.log('✅ Evento enviado exitosamente a Meta Conversions API:', result);
            return result;

        } catch (error) {
            const isNetworkError = error.name === 'TypeError' && error.message.includes('fetch');
            const isTimeoutError = error.name === 'AbortError';
            const isConnectivityError = error.message.includes('ERR_INTERNET_DISCONNECTED') ||
                error.message.includes('Failed to fetch') ||
                isNetworkError || isTimeoutError;

            console.error(`🚨 Error enviando evento (intento ${retryCount + 1}):`, {
                eventName,
                errorType: error.name,
                errorMessage: error.message,
                isNetworkError,
                isTimeoutError,
                isConnectivityError,
                retryCount
            });

            // Retry logic para errores de conectividad
            if (isConnectivityError && retryCount < maxRetries) {
                console.log(`🔄 Reintentando en ${retryDelay}ms... (${retryCount + 1}/${maxRetries})`);

                await new Promise(resolve => setTimeout(resolve, retryDelay));
                return this.sendConversionEvent(eventName, eventData, retryCount + 1);
            }

            // Si agotamos los retries, guardar para envío posterior
            if (retryCount >= maxRetries) {
                console.warn('⚠️ Máximo de reintentos alcanzado. Guardando evento para envío posterior.');
                this.queueFailedEvent(eventName, eventData, error);
            }

            // No interrumpir la experiencia del usuario
            return null;
        }
    }

    /**
     * Guardar eventos fallidos para retry posterior
     */
    queueFailedEvent(eventName, eventData, error) {
        try {
            const failedEvents = JSON.parse(localStorage.getItem('metaPixel_failedEvents') || '[]');
            const failedEvent = {
                eventName,
                eventData,
                timestamp: Date.now(),
                error: error.message,
                retryCount: 0
            };

            failedEvents.push(failedEvent);

            // Mantener solo los últimos 10 eventos fallidos
            if (failedEvents.length > 10) {
                failedEvents.splice(0, failedEvents.length - 10);
            }

            localStorage.setItem('metaPixel_failedEvents', JSON.stringify(failedEvents));
            console.log('📦 Evento guardado para retry posterior:', failedEvent);
        } catch (storageError) {
            console.error('Error guardando evento fallido:', storageError);
        }
    }

    /**
     * Intentar reenviar eventos fallidos
     */
    async retryFailedEvents() {
        try {
            const failedEvents = JSON.parse(localStorage.getItem('metaPixel_failedEvents') || '[]');
            if (failedEvents.length === 0) return;

            console.log(`🔄 Intentando reenviar ${failedEvents.length} eventos fallidos...`);

            const successfulEvents = [];

            for (const event of failedEvents) {
                // Solo reintentar eventos de menos de 1 hora
                if (Date.now() - event.timestamp < 3600000) {
                    const result = await this.sendConversionEvent(event.eventName, event.eventData, 0);
                    if (result) {
                        successfulEvents.push(event);
                    }
                }
            }

            // Remover eventos exitosos de la cola
            if (successfulEvents.length > 0) {
                const remainingEvents = failedEvents.filter(event => !successfulEvents.includes(event));
                localStorage.setItem('metaPixel_failedEvents', JSON.stringify(remainingEvents));
                console.log(`✅ ${successfulEvents.length} eventos reenviados exitosamente.`);
            }
        } catch (error) {
            console.error('Error reintentando eventos fallidos:', error);
        }
    }

    /**
     * Preparar datos del usuario para Meta
     */
    prepareUserData() {
        const userData = {};

        // Solo incluir campos que Meta reconoce y que tenemos datos válidos
        if (this.userTrackingData.client_ip_address) {
            userData.client_ip_address = this.userTrackingData.client_ip_address;
        }

        if (this.userTrackingData.client_user_agent) {
            userData.client_user_agent = this.userTrackingData.client_user_agent;
        }

        if (this.userTrackingData.fbp) {
            userData.fbp = this.userTrackingData.fbp;
        }

        if (this.userTrackingData.fbc) {
            userData.fbc = this.userTrackingData.fbc;
        }

        // Email - solo si es válido
        if (this.userTrackingData.email && this.isValidEmail(this.userTrackingData.email)) {
            userData.em = [this.hashData(this.userTrackingData.email)];
        }

        // Teléfono - solo si existe y está normalizado
        if (this.userTrackingData.phone) {
            const normalizedPhone = this.normalizePhone(this.userTrackingData.phone);
            if (normalizedPhone && normalizedPhone.length >= 10) {
                userData.ph = [this.hashData(normalizedPhone)];
            }
        }

        // Nombre - solo si existe
        if (this.userTrackingData.firstName) {
            userData.fn = [this.hashData(this.userTrackingData.firstName)];
        }

        // Apellido - solo si existe
        if (this.userTrackingData.lastName) {
            userData.ln = [this.hashData(this.userTrackingData.lastName)];
        }

        return userData;
    }

    /**
     * Hash de datos para Meta (preparación en frontend)
     */
    hashData(data) {
        if (!data) return null;
        // Normalizar datos antes de enviar al servidor
        return data.toString().toLowerCase().trim().replace(/\s+/g, ' ');
    }

    /**
     * Validar formato de email
     */
    isValidEmail(email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    }

    /**
     * Normalizar número de teléfono
     */
    normalizePhone(phone) {
        if (!phone) return null;
        // Remover caracteres especiales y espacios
        return phone.replace(/[^\d+]/g, '');
    }

    /**
     * Actualizar datos del usuario conforme completa el formulario
     */
    updateUserData(userData) {
        // Normalizar y validar datos antes de guardar
        const normalizedData = {};

        if (userData.email && this.isValidEmail(userData.email)) {
            normalizedData.email = this.hashData(userData.email);
        }

        if (userData.phone) {
            normalizedData.phone = this.normalizePhone(userData.phone);
        }

        if (userData.firstName) {
            normalizedData.firstName = this.hashData(userData.firstName);
        }

        if (userData.lastName) {
            normalizedData.lastName = this.hashData(userData.lastName);
        }

        if (userData.city) {
            normalizedData.city = this.hashData(userData.city);
        }

        if (userData.country) {
            normalizedData.country = this.hashData(userData.country);
        }

        this.userTrackingData = {
            ...this.userTrackingData,
            ...normalizedData
        };

        // Guardar en localStorage para persistencia
        localStorage.setItem('transferIsOk_user_tracking', JSON.stringify(this.userTrackingData));

        console.log('✅ Datos de usuario actualizados y normalizados:', normalizedData);
    }

    /**
     * Trackear interacción con formulario
     */
    trackFormInteraction(formStep, action, fieldName = null) {
        console.log('Form Interaction:', { formStep, action, fieldName });

        const eventData = {
            form_step: formStep,
            action: action,
            field_name: fieldName,
            timestamp: Date.now()
        };

        this.trackCustomEvent('FormInteraction', eventData);
    }

    /**
     * Trackear progreso del formulario
     */
    trackFormProgress(formStep, completedFields, totalFields) {
        console.log('Form Progress:', { formStep, completedFields, totalFields });

        const progress = Math.round((completedFields / totalFields) * 100);

        const eventData = {
            form_step: formStep,
            completed_fields: completedFields,
            total_fields: totalFields,
            progress_percentage: progress
        };

        this.trackCustomEvent('FormProgress', eventData);
    }

    /**
     * Obtener datos de tracking almacenados
     */
    getStoredTrackingData() {
        const campaignData = localStorage.getItem('transferIsOk_campaign_data');
        const userTrackingData = localStorage.getItem('transferIsOk_user_tracking');

        return {
            campaign: campaignData ? JSON.parse(campaignData) : null,
            user: userTrackingData ? JSON.parse(userTrackingData) : null
        };
    }

    /**
     * Limpiar datos de tracking
     */
    clearTrackingData() {
        localStorage.removeItem('transferIsOk_campaign_data');
        localStorage.removeItem('transferIsOk_user_tracking');
        this.userTrackingData = {};
    }
}

// Instancia global del servicio
const metaPixelService = new MetaPixelService();

export default metaPixelService; 